import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { DISEASE_OPTION_KIND, sameDiseaseName } from 'src/util/diseaseSearch';

const CHIP_SX = { height: 18, fontSize: 10, fontFamily: 'monospace' };

/**
 * One row of a disease search dropdown, in every disease picker
 * (TGCLOUD-1737, and TGCLOUD-1892 for the six pickers).
 *
 * What the clinician is about to record must be visible before they record
 * it, so the row always shows:
 *
 *   - the name, which is the preferred code's own name;
 *   - the code that will be stored and its vocabulary, or, on a row holding
 *     the disease in several vocabularies, a chip per code, the preferred one
 *     first;
 *   - "(category)" on an ICD-10-CM category heading (Q90, Q87.4), which is a
 *     code but not the most specific one;
 *   - the Mondo concept the row stands for, and Mondo's name for it where it
 *     differs ("Breast cancer", OMIM:114480, is Mondo's hereditary breast
 *     carcinoma);
 *   - what the words matched, when it is not the name shown: another code's
 *     name ("Fragile X chromosome"), or a Mondo synonym ("HNPCC");
 *   - that a row was reached only through a curated bridge (the same condition
 *     in another vocabulary, so "Ovarian cancer" under "breast cancer" says
 *     why), and that the rows are HPO phenotypes when no disease matched.
 *
 * `chooser` says whether picking a merged row asks which code to record (the
 * pedigree's Diagnoses add and the re-code dialog) or records the preferred
 * one. `showKind` is off where a group header already names the kind
 * (Settings → Family History → Disease list).
 */
export default function DiagnosisOptionRow({
  option,
  chooser = false,
  showKind = true
}) {
  const t = useAutoTranslation();
  const codes = option.codingSystems?.length
    ? option.codingSystems
    : [{ id: option.id, label: option.source }];
  const [primary] = codes;
  const merged = codes.length > 1;
  const category = (cs) => cs?.isLeaf === false;

  const notes = [];
  if (option.viaXref)
    notes.push(t('Related: the same condition in another vocabulary'));
  if (showKind && option.kind === DISEASE_OPTION_KIND.phenotype)
    notes.push(t('A phenotype (HPO), not a disease: no disease matched'));
  if (option.matched?.kind === 'name' && option.matched.source)
    notes.push(
      t('Found by its {{source}} name: {{value}}', {
        source: option.matched.source,
        value: option.matched.value
      })
    );
  else if (option.matched?.kind === 'mondo-synonym')
    notes.push(
      t('Found by a Mondo synonym: {{value}}', { value: option.matched.value })
    );
  else if (option.matched?.kind === 'mondo-name')
    notes.push(
      t('Found by its Mondo name: {{value}}', { value: option.matched.value })
    );
  if (option.mondo?.label && !sameDiseaseName(option.mondo.label, option.name))
    notes.push(t('Mondo: {{label}}', { label: option.mondo.label }));
  if (option.replaces && option.mondo?.id)
    notes.push(
      t('{{old}} is obsolete in Mondo; this is {{current}}', {
        old: option.replaces,
        current: option.mondo.id
      })
    );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Typography variant="body1">{option.name}</Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 0.25,
          flexWrap: 'wrap'
        }}
      >
        {merged ? (
          codes.map((cs, i) => (
            <Chip
              key={cs.id}
              data-testid={`diagnosis-code-${cs.id}`}
              label={category(cs) ? `${cs.id} ${t('(category)')}` : cs.id}
              title={cs.label}
              size="small"
              variant="outlined"
              color={i === 0 ? 'primary' : 'default'}
              sx={CHIP_SX}
            />
          ))
        ) : (
          <>
            <Typography
              variant="caption"
              data-testid={`diagnosis-code-${primary.id}`}
              sx={{ color: 'text.secondary', fontFamily: 'monospace' }}
            >
              {primary.id}
            </Typography>
            {category(primary) && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('(category)')}
              </Typography>
            )}
            {primary.label && (
              <>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {'·'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {primary.label}
                </Typography>
              </>
            )}
          </>
        )}
        {/* An older backend sends a merged row's vocabularies with no codes. */}
        {!merged &&
          option.hasDuplicates &&
          (option.sources || [])
            .filter((s) => s !== primary.label)
            .map((source) => (
              <Chip
                key={source}
                label={source}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: 10 }}
              />
            ))}
        {merged && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {chooser
              ? t('· choose the code to record')
              : t('· records {{code}}', { code: primary.id })}
          </Typography>
        )}
        {option.mondo?.id && (
          <Chip
            label={option.mondo.id}
            // Mondo is CC BY 4.0: credited where its content is shown, as
            // Settings → Terminology → Data sources credits it in full.
            title={t('Mondo Disease Ontology concept (CC BY 4.0)')}
            size="small"
            variant="outlined"
            sx={{ ...CHIP_SX, borderStyle: 'dashed' }}
          />
        )}
      </Box>
      {notes.map((note) => (
        <Typography
          key={note}
          variant="caption"
          sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
        >
          {note}
        </Typography>
      ))}
    </Box>
  );
}
